/* klaasje-header
   ---------------
   Scroll-toestand van de chrome-pill + de menu-drawer (openen, sluiten,
   focus vasthouden, scroll-lock). De winkelwagenknop hoeft hier niets:
   die draagt id="cart-icon-bubble", waar Dawn's cart-drawer zichzelf
   aan koppelt. Vervangen we de drawer later door een eigen versie, dan
   blijft dezelfde knop werken.
*/

class KlaasjeHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('[data-klaasje-header]');
    this.menu = this.querySelector('[data-klaasje-menu]');
    this.overlay = this.querySelector('[data-klaasje-menu-overlay]');
    this.openBtn = this.querySelector('[data-klaasje-menu-open]');
    this.closeBtn = this.querySelector('[data-klaasje-menu-close]');

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onMenuClick = this.onMenuClick.bind(this);

    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.openBtn) this.openBtn.addEventListener('click', this.open);
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.close);
    if (this.overlay) this.overlay.addEventListener('click', this.close);
    if (this.menu) this.menu.addEventListener('click', this.onMenuClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('overflow-hidden');
  }

  /* Voorbij de hero-rand wisselt de pill van transparant naar dicht chrome.
     Op pagina's zonder donkere hero (geen html.klaasje-hero-dark) doet
     deze class niets — daar is dicht chrome de standaard. */
  onScroll() {
    if (!this.header) return;
    this.header.classList.toggle('is-scrolled', window.scrollY > 40);
  }

  onKeydown(event) {
    if (!this.isOpen()) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  isOpen() {
    return this.menu ? this.menu.classList.contains('is-open') : false;
  }

  open() {
    if (!this.menu) return;
    this.menu.classList.add('is-open');
    this.menu.removeAttribute('aria-hidden');
    if (this.overlay) this.overlay.classList.add('is-open');
    if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    // wachten tot de drawer in beeld schuift voelt trager dan het is;
    // focus direct verzetten houdt toetsenbordgebruik gelijk aan muis
    if (this.closeBtn) this.closeBtn.focus();
  }

  close(opties) {
    if (!this.menu) return;
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.classList.remove('is-open');
    if (this.openBtn) {
      this.openBtn.setAttribute('aria-expanded', 'false');
      // Sluit de drawer omdat je een link koos, dan hoort de focus niet
      // terug naar de hamburger te springen: je gaat juist ergens heen.
      if (!(opties && opties.houdFocus)) this.openBtn.focus();
    }
    document.body.classList.remove('overflow-hidden');
  }

  /* ── Een link naar een sectie op deze pagina ─────────────────
     Wijst een menu-item naar een anker op de pagina waar je al staat
     (`koop nu` naar #buy), dan is er niets te navigeren: dan hoort de
     drawer weg te gaan en de pagina vanaf waar je bent naar die sectie
     te schuiven. Alles wat naar een andere pagina wijst laten we met
     rust, dat doet de browser beter zelf. */
  onMenuClick(event) {
    const link = event.target.closest('a[href]');
    if (!link || !this.menu.contains(link)) return;

    const doel = this.ankerOpDezePagina(link);
    if (!doel) return;

    event.preventDefault();
    this.close({ houdFocus: true });

    // De drawer zet `overflow-hidden` op de body; zolang die staat kan er
    // niet gescrold worden. Eén frame wachten is genoeg.
    requestAnimationFrame(() => {
      const stil = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      doel.scrollIntoView({ behavior: stil ? 'auto' : 'smooth', block: 'start' });
      // De sectie draagt zelf een scroll-margin, dus hij komt onder de
      // zwevende pill uit.
    });
  }

  ankerOpDezePagina(link) {
    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return null;
    }
    if (!url.hash || url.pathname !== window.location.pathname) return null;
    try {
      return document.querySelector(url.hash);
    } catch (e) {
      return null;
    }
  }

  trapFocus(event) {
    const focusable = this.menu.querySelectorAll('a[href], button:not([disabled])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('klaasje-header')) {
  customElements.define('klaasje-header', KlaasjeHeader);
}
